import { TimelineShift } from 'api/shift'
import { Company, Position, User, Work, WorkShift } from '../typings/common_defs'
import { differenceInMinutes } from 'date-fns'
import { CompanyInfo } from 'api/company'
import { getLongDuration } from './time'
import { toFirstName, toTitleCase } from './string'
import {
  AssignedWorkerOptionsEnum,
  PRORATED_PAY_OPTIONS,
} from 'lib/work/assigned_work_options'
import { calculateEstimatedTimeWorkedInMinutes } from './work'

export const getTakePercentage = (company: Company | CompanyInfo) => {
  return company.takePercentageEstimate || 20
}

export const getChargeRate = (company: Company | CompanyInfo) => {
  return 1 + getTakePercentage(company) / 100
}

export const getShiftBonus = (shift: WorkShift | TimelineShift) => {
  return shift.shiftBonuses && shift.shiftBonuses.length > 0
    ? Number(shift.shiftBonuses[shift.shiftBonuses.length - 1].amount)
    : null
}

export const getCompanyPaidShiftBonus = (
  shift: WorkShift | TimelineShift
) => {
  const companyPaidBonuses = (shift.shiftBonuses || []).filter(
    (bonus) => bonus.companyPaid
  )
  if (companyPaidBonuses.length === 0) return null
  return Number(companyPaidBonuses[companyPaidBonuses.length - 1].amount)
}

export const getShiftPay = (shift: WorkShift | TimelineShift) => {
  return shift.payLumpSum
    ? Number(shift.payLumpSum).toFixed(2)
    : Number(shift.payRate).toFixed(2)
}

export const getShiftBillRate = (shift: WorkShift | TimelineShift) => {
  return shift.billRate ? Number(shift.billRate).toFixed(2): 0
}

export const isBillRateShift = (shift: WorkShift | TimelineShift | null | undefined) => {
  return shift?.billRate !== undefined && shift?.billRate !== null
}

export const getPositionBillRate = (position: Position | null | undefined) => {
  return position?.billRate ? Number(position.billRate).toFixed(2): 0
}

export const isBillRatePosition = (position: Position | null | undefined) => {
  return position?.billRate !== undefined && position?.billRate !== null
}

export const calculateEstimatedPay = (
  work: Work,
  shift: TimelineShift
): number => {
  // if the worker is marked bailed, return 0
  if (work.status === 'bailed' || work.supervisorMarkedNoShow) return 0

  // if lump sum, return lump sum + bonus
  if (shift.payLumpSum) return Number(shift.payLumpSum)

  // TODO: handle lump sum partial pay, once we support it

  const hourlyRate = Number(shift.payRate)
  return hourlyRate * getEstimatedBillableHours(work, shift)
}

/**
 * Billable hours for pay/charge estimates — shared by worker pay and bill-rate
 * charge so hours are counted the same either way.
 */
const getEstimatedBillableHours = (
  work: Work,
  shift: WorkShift | TimelineShift
): number => {
  const { startedAt, completedAt } = work
  const { startsAt, endsAt } = shift
  if (!startedAt || !completedAt || !startsAt || !endsAt) {
    return 0
  }

  const shiftStartDate = new Date(startsAt)
  const shiftEndDate = new Date(endsAt)
  let hoursWorked =
    calculateEstimatedTimeWorkedInMinutes({ startedAt, completedAt }) / 60 || 0
  const minPayLengthMins = shift.minimumPayPolicy?.minPayLength

  let shouldProrate =
    work.finishedEarlyCategory &&
    PRORATED_PAY_OPTIONS.includes(
      work.finishedEarlyCategory as AssignedWorkerOptionsEnum
    )

  let fullShiftHours = differenceInMinutes(shiftEndDate, shiftStartDate) / 60

  if (minPayLengthMins) {
    const isUnderMinHours = hoursWorked < minPayLengthMins / 60
    if (isUnderMinHours) {
      fullShiftHours = minPayLengthMins / 60
    } else {
      shouldProrate = true
    }
  }

  if (shift.lunchLength) {
    fullShiftHours = Math.max(0, fullShiftHours - shift.lunchLength / 60)
    hoursWorked = Math.max(0, hoursWorked - shift.lunchLength / 60)
  }

  return shouldProrate ? hoursWorked : fullShiftHours
}

/**
 * Get the text shown for estimated pay for a work
 * @param user
 * @param shift
 * @returns
 */
export const getMinimumPayText = (
  user: User | undefined,
  shift: TimelineShift | undefined
) => {
  const minimumPayMessage = shift?.minimumPayPolicy?.minPayLength
    ? `the first ${getLongDuration(
        shift.minimumPayPolicy.minPayLength
      )} as minimum pay as this shift has a ${getLongDuration(
        shift.minimumPayPolicy.minPayLength
      )} Minimum Pay Policy in place.`
    : 'full shift pay (adjusted if started late)'
  const userName = user ? toTitleCase(toFirstName(user?.name)) : 'Worker'

  return `
      ${userName} completed assigned work and/or was dismissed early by a Supervisor. ${userName} will receive ${minimumPayMessage}.
    `
}

export const calculateBillRateCharge = (params: {
  billRate: number
  numWorkers: number
  hours: number
}) => {
  const { billRate, numWorkers, hours } = params
  return billRate * numWorkers * hours
}

export const formatBillRatePerWorker = (billRate: number | undefined | null) => {
  return `$${Number(billRate).toFixed(2)}/hr per worker`
}

export type BillRateShiftPaymentSummary = {
  numWorkers: number
  totalShiftHours: string
  billRate: number
  baseCharge: number
  bonusPerWorker: number
  bonusWWFee: number
  bonusWWFeePercent: number
  totalBonus: number
  total: number
}

const isBillableWork = (work: Work) => {
  if (work.status === 'bailed' || work.supervisorMarkedNoShow) return false
  if (!work.startedAt || !work.completedAt) return false
  return true
}

export const getBillRateShiftPaymentSummary = (params: {
  shift: WorkShift | TimelineShift
  wwFeePercent: number
}): BillRateShiftPaymentSummary | null => {
  const { shift, wwFeePercent } = params
  if (!isBillRateShift(shift)) return null

  const billRate = Number(shift.billRate)

  const billableWork = (shift.work || []).filter(isBillableWork)
  if (billableWork.length === 0) return null

  const hoursList = billableWork.map((work) =>
    getEstimatedBillableHours(work, shift)
  )
  const totalHours = hoursList.reduce((sum, hours) => sum + hours, 0)
  if (!totalHours) return null

  const numWorkers = billableWork.length
  const baseCharge = billRate * totalHours
  const bonusPerWorker = getCompanyPaidShiftBonus(shift) || 0
  const bonusWWFee = (wwFeePercent / 100) * bonusPerWorker * numWorkers
  const totalBonus = bonusPerWorker * numWorkers + bonusWWFee

  return {
    numWorkers,
    totalShiftHours: totalHours.toFixed(2),
    billRate,
    baseCharge,
    bonusPerWorker,
    bonusWWFee,
    bonusWWFeePercent: wwFeePercent,
    totalBonus,
    total: baseCharge + totalBonus,
  }
}


